import React, { useMemo, useState } from "react";
import logo from "assets/images/login-image.png";
import Input from "components/common/Input";
import Button from "components/common/Button";
import api from "config/api";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "hooks/store";
import { setAdminData } from "store/slices/admin.slice";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import HomeIcon from "@mui/icons-material/Home";
import { useCachedImage } from "hooks/useCachedImage";
import { resolveUploadAssetUrl } from "utils/media";

const LoginAdmin = () => {
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [data, setData] = useState({
    userName: "",
    password: "",
  });
  const { settings, companyData } = useAppSelector(({ main }) => main);
  const resolvedLogoUrl = useMemo(
    () =>
      settings?.showLogo !== 0 && companyData?.logoUrl
        ? resolveUploadAssetUrl(companyData.logoUrl)
        : logo,
    [companyData?.logoUrl, settings?.showLogo]
  );
  const logoUrl = useCachedImage(resolvedLogoUrl, { cacheKey: "company-logo" });

  const handleSubmit = () => {
    toast.dismiss();
    if (!data.userName) {
      toast.error("UserName is required");
      return;
    }
    if (!data.password) {
      toast.error("Password is required");
      return;
    }
    setLoading(true);
    api(`/api/User/ValidateUser?${new URLSearchParams(data)}`)
      .then(({ data }) => {
        dispatch(setAdminData(data));
        toast.success("Login Successfully");
        navigate("/admin/setting/time");
      })
      .catch((e) => {
        toast.error("Invalid Username or Password");
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden px-4 py-6">
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.34),_transparent_42%),linear-gradient(140deg,rgba(255,255,255,0.12),transparent_58%)]" />

      <div className="relative z-10 mx-auto flex w-full max-w-6xl items-center justify-end">
        <button
          className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/60 bg-white/80 shadow-[0_10px_24px_rgba(15,23,42,0.12)]"
          onClick={() => {
            navigate("/");
            window.location.reload();
          }}
          aria-label="Go home"
        >
          <HomeIcon className="!text-black cursor-pointer" />
        </button>
      </div>

      <div className="relative z-10 mx-auto flex min-h-[calc(100vh-72px)] w-full max-w-6xl items-center justify-center">
        <div
          className="w-full overflow-hidden rounded-[30px] border border-white/55 shadow-[0_28px_80px_rgba(15,23,42,0.18)]"
          style={{
            background: "linear-gradient(135deg, rgb(255 255 255 / 0.93), rgb(255 255 255 / 0.86))",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
            fontFamily: '"Noto Sans", sans-serif',
          }}
        >
          <div className="grid lg:grid-cols-[1.08fr_0.92fr]">
            <div className="relative hidden min-h-[580px] items-center justify-center px-10 py-10 lg:flex">
              <div className="absolute inset-0 bg-[linear-gradient(160deg,rgba(255,255,255,0.3),rgba(var(--color-primary),0.09))]" />
              <div className="relative z-10 flex max-w-[420px] flex-col items-center text-center">
                <div className="rounded-[28px] bg-white/72 px-8 py-8 shadow-[0_20px_50px_rgba(15,23,42,0.08)]">
                  {settings?.showLogo !== 0 ? (
                    <img key={logoUrl} src={logoUrl} alt="" className="h-[120px] w-[120px] rounded-[24px] object-contain" />
                  ) : (
                    <div className="flex h-[120px] w-[120px] items-center justify-center rounded-[24px] bg-primary/10">
                      <AdminPanelSettingsIcon className="!text-[56px] text-primary" />
                    </div>
                  )}
                </div>

                <div className="mt-8">
                  <p className="text-[12px] font-[700] uppercase tracking-[0.18em] text-primary/80">
                    Admin Access
                  </p>
                  <h1 className="mt-3 text-[34px] font-[900] leading-[1.05] tracking-[-0.04em] text-slate-900">
                    Manage Ranger Online with a cleaner workspace.
                  </h1>
                  <p className="mt-3 text-[15px] leading-6 text-slate-600">
                    Sign in to update business hours, settings, and customer-facing content.
                  </p>
                </div>
              </div>
            </div>

            <div className="px-5 py-6 sm:px-8 lg:px-10 lg:py-10">
              <div className="mx-auto w-full max-w-[400px]">
                <div className="mb-5 flex items-center justify-center lg:hidden">
                  {settings?.showLogo !== 0 ? (
                    <img key={logoUrl} src={logoUrl} alt="" className="h-16 w-16 rounded-2xl object-contain" />
                  ) : (
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
                      <AdminPanelSettingsIcon className="text-primary" />
                    </div>
                  )}
                </div>

                <div className="rounded-[24px] border border-white/65 bg-white/78 p-5 shadow-[0_18px_42px_rgba(15,23,42,0.08)] sm:p-6">
                  <p className="text-[12px] font-[700] uppercase tracking-[0.16em] text-primary/80">
                    Ranger Admin
                  </p>
                  <h2 className="mt-2 text-[28px] font-[900] leading-[1.05] tracking-[-0.04em] text-slate-900">
                    Login Admin
                  </h2>
                  <p className="mt-3 text-[14px] leading-6 text-slate-600">
                    Access the control panel for {!!settings.ShowCompanyName && !!companyData?.companyName ? companyData.companyName : "Ranger Online"}.
                  </p>

                  <div className="mt-6 flex flex-col gap-4">
                    <Input
                      size="small"
                      label="Username"
                      variant="outlined"
                      value={data.userName}
                      sx={{
                        width: "100%",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "14px",
                          backgroundColor: "rgba(255,255,255,0.78)",
                        },
                      }}
                      onValueChange={(userName) => setData((e) => ({ ...e, userName }))}
                    />
                    <Input
                      size="small"
                      label="Password"
                      type="password"
                      variant="outlined"
                      value={data.password}
                      sx={{
                        width: "100%",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "14px",
                          backgroundColor: "rgba(255,255,255,0.78)",
                        },
                      }}
                      onValueChange={(password) => setData((e) => ({ ...e, password }))}
                    />
                  </div>

                  <Button
                    disabled={loading}
                    className="!mt-6 !w-full !rounded-[16px] !py-3.5 !text-[15px] !font-[800]"
                    onClick={handleSubmit}
                  >
                    Login
                  </Button>

                  {!!settings.ShowCompanyName && !!companyData?.companyName && (
                    <p className="mt-5 text-center text-[13px] font-[600] text-slate-500">
                      {companyData.companyName}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginAdmin;
